import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	FieldDefinition,
	FieldType,
} from "../../../domain/treatmentPlan.interface";

interface FieldDefinitionsEditorProps {
	fields: FieldDefinition[];
	onChange: (fields: FieldDefinition[]) => void;
}

const FIELD_TYPES: Array<{ value: FieldType; label: string }> = [
	{ value: "text", label: "Text" },
	{ value: "time", label: "Time" },
	{ value: "number", label: "Number" },
	{ value: "select", label: "Select" },
	{ value: "date", label: "Date" },
	{ value: "timezone", label: "Timezone" },
];

const emptyField = (): FieldDefinition => ({
	fieldName: "",
	fieldLabel: "",
	fieldType: "text",
	required: false,
});

const FieldDefinitionsEditor: React.FC<FieldDefinitionsEditorProps> = ({
	fields,
	onChange,
}) => {
	const [showAddForm, setShowAddForm] = useState(false);
	const [newField, setNewField] = useState<FieldDefinition>(emptyField());

	const handleAdd = () => {
		if (!newField.fieldName.trim() || !newField.fieldLabel.trim()) return;
		onChange([...fields, newField]);
		setNewField(emptyField());
		setShowAddForm(false);
	};

	const handleRemove = (index: number) => {
		onChange(fields.filter((_, i) => i !== index));
	};

	return (
		<Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="subtitle1">Required Enrollment Fields</Typography>
				<Button
					size="small"
					startIcon={<AddIcon />}
					onClick={() => setShowAddForm(true)}
				>
					Add Field
				</Button>
			</Box>
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mb: 2 }}
			>
				Define fields that must be collected during enrollment. These can be
				referenced in Smart Program workflows using {"{{enrollment.fieldName}}"}{" "}
				syntax.
			</Typography>

			{fields.length > 0 && (
				<TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Field Name</TableCell>
								<TableCell>Label</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Required</TableCell>
								<TableCell width={60}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{fields.map((field, index) => (
								<TableRow key={index}>
									<TableCell>
										<code>{field.fieldName}</code>
									</TableCell>
									<TableCell>{field.fieldLabel}</TableCell>
									<TableCell>{field.fieldType}</TableCell>
									<TableCell>{field.required ? "Yes" : "No"}</TableCell>
									<TableCell>
										<IconButton
											size="small"
											color="error"
											onClick={() => handleRemove(index)}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{fields.length === 0 && !showAddForm && (
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ textAlign: "center", py: 2 }}
				>
					No enrollment fields defined yet.
				</Typography>
			)}

			{showAddForm && (
				<Box sx={{ border: "1px dashed #ccc", borderRadius: 1, p: 2, mt: 1 }}>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							flexWrap: "wrap",
							alignItems: "flex-end",
						}}
					>
						<TextField
							size="small"
							label="Field Name"
							value={newField.fieldName}
							onChange={(e) =>
								setNewField({
									...newField,
									fieldName: e.target.value.replace(/\s/g, ""),
								})
							}
							placeholder="e.g. dosageTime"
							helperText="camelCase, no spaces"
							sx={{ flex: 1, minWidth: 150 }}
						/>
						<TextField
							size="small"
							label="Label"
							value={newField.fieldLabel}
							onChange={(e) =>
								setNewField({ ...newField, fieldLabel: e.target.value })
							}
							placeholder="e.g. Preferred Dosage Time"
							sx={{ flex: 1, minWidth: 150 }}
						/>
						<FormControl size="small" sx={{ minWidth: 120 }}>
							<InputLabel>Type</InputLabel>
							<Select
								value={newField.fieldType}
								label="Type"
								onChange={(e) =>
									setNewField({
										...newField,
										fieldType: e.target.value as FieldType,
									})
								}
							>
								{FIELD_TYPES.map((t) => (
									<MenuItem key={t.value} value={t.value}>
										{t.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={newField.required}
									onChange={(e) =>
										setNewField({
											...newField,
											required: e.target.checked,
										})
									}
								/>
							}
							label="Required"
						/>
					</Box>
					{newField.fieldType === "select" && (
						<TextField
							size="small"
							fullWidth
							label="Options (comma-separated)"
							value={newField.options?.join(", ") || ""}
							onChange={(e) =>
								setNewField({
									...newField,
									options: e.target.value
										.split(",")
										.map((s) => s.trim())
										.filter(Boolean),
								})
							}
							placeholder="e.g. Option A, Option B, Option C"
							sx={{ mt: 2 }}
						/>
					)}
					<Box sx={{ display: "flex", gap: 1, mt: 2 }}>
						<Button size="small" variant="contained" onClick={handleAdd}>
							Add
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={() => {
								setShowAddForm(false);
								setNewField(emptyField());
							}}
						>
							Cancel
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default FieldDefinitionsEditor;
